export const DEFAULT_OVERLAY_STYLE = 'vignette_subtle';

export const OVERLAY_STYLES = [
  'vignette_subtle',
  'vignette_standard',
  'vignette_strong',
  'scrim',
  'split_blur',
] as const;

export type OverlayStyle = (typeof OVERLAY_STYLES)[number];

export const isSupportedOverlayStyle = (
  value: unknown,
): value is OverlayStyle =>
  typeof value === 'string' &&
  (OVERLAY_STYLES as readonly string[]).includes(value);

// Bottom vignette: transparent at `clear`, easing to `floor` alpha at the bottom
// edge. All three tiers share this builder so retuning one is a data change.
const vignette = (
  clear: number,
  mid: [number, number],
  late: [number, number],
  floor: number,
): string =>
  `linear-gradient(to bottom, rgba(0, 0, 0, 0) ${clear}%,` +
  ` rgba(0, 0, 0, ${mid[0]}) ${mid[1]}%,` +
  ` rgba(0, 0, 0, ${late[0]}) ${late[1]}%,` +
  ` rgba(0, 0, 0, ${floor}) 100%)`;

const BACKGROUNDS: Record<OverlayStyle, string> = {
  vignette_subtle: vignette(50, [0.4, 72], [0.7, 88], 0.85),
  vignette_standard: vignette(40, [0.6, 70], [0.88, 88], 0.95),
  vignette_strong: vignette(30, [0.75, 62], [0.96, 85], 1),
  // The only style that still degrades the image after the top half is cropped away.
  scrim: 'rgba(0, 0, 0, 0.3)',
  // Draws its own blurred layer; the shared overlay div stays transparent.
  split_blur: 'none',
};

export const resolveOverlayStyle = (value: unknown): OverlayStyle =>
  isSupportedOverlayStyle(value) ? value : DEFAULT_OVERLAY_STYLE;

export const overlayBackgroundFor = (value: unknown): string =>
  BACKGROUNDS[resolveOverlayStyle(value)];

export const usesSplitBlur = (value: unknown): boolean =>
  resolveOverlayStyle(value) === 'split_blur';
